/**
 * CreatePageDialog — modal that creates a new notebook page.
 *
 * The parent selector is a flat dropdown of every page in the workspace
 * (with depth-aware indentation) so a new page can be nested under any
 * existing page. The icon picker is the shared `EmojiPicker` (same
 * catalogue as `PageHeader`). The title gets an auto-derived slug via
 * `slugifyPageTitle`.
 *
 * Submit goes through `useCreatePage`; the parent route navigates to the
 * newly-created page on success.
 */

import { useEffect, useRef, useState } from 'react'
import { useCreatePage, usePageTree } from '@/features/notebook/pages'
import { flattenTree, buildTree, type PageNode } from '@/features/notebook/tree'
import { slugifyPageTitle } from '@/features/notebook/slugify'
import { EmojiPicker } from './EmojiPicker'

export interface CreatePageDialogProps {
  wsId: string
  /** Default parent — usually the currently-open page, or null for root. */
  defaultParentId?: string | null
  onClose: () => void
  onCreated?: (pageId: string) => void
}

export function CreatePageDialog({ wsId, defaultParentId, onClose, onCreated }: CreatePageDialogProps) {
  const [title, setTitle] = useState('')
  const [icon, setIcon] = useState('📄')
  const [parentId, setParentId] = useState<string | null>(defaultParentId ?? null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const iconButtonRef = useRef<HTMLButtonElement | null>(null)
  const tree = usePageTree(wsId)
  const create = useCreatePage(wsId)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const flat = flattenTree(buildTree(tree.data ?? []))

  const submit = async () => {
    const trimmed = title.trim() || 'Untitled'
    try {
      const page = await create.mutateAsync({
        title: trimmed,
        icon,
        parentPageId: parentId,
        slug: slugifyPageTitle(trimmed),
      })
      onCreated?.(page.id)
      onClose()
    } catch {
      // The mutation hook surfaces errors via toast upstream; nothing to do.
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Create a new notebook page"
      >
        <div className="mb-3 flex items-center gap-2">
          <button
            ref={iconButtonRef}
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label="Pick an icon"
            className="flex h-9 w-9 items-center justify-center rounded border border-surface-200 text-xl hover:bg-surface-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-surface-700 dark:hover:bg-surface-800"
          >
            {icon}
          </button>
          <input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Untitled"
            className="input flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit()
            }}
          />
        </div>
        <label className="mb-1 block text-xs text-surface-500">Under</label>
        <select
          value={parentId ?? ''}
          onChange={(e) => setParentId(e.target.value === '' ? null : e.target.value)}
          className="input mb-4 w-full"
        >
          <option value="">Workspace root</option>
          {flat.map(({ page, level }) => (
            <option key={page.id} value={page.id}>
              {'· '.repeat(level)}
              {page.icon ? `${page.icon} ` : ''}
              {page.title}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={() => void submit()} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create page'}
          </button>
        </div>
      </div>
      {pickerOpen && iconButtonRef.current && (
        <EmojiPicker
          value={icon}
          onChange={setIcon}
          onClose={() => setPickerOpen(false)}
          anchorEl={iconButtonRef.current}
        />
      )}
    </div>
  )
}

// Re-export the tree node type so other files in the notebook feature don't
// have to dig through the tree module to find it.
export type { PageNode }
